import { isKnownRoute, WEB_ROUTE_PATHS } from '../../webRoutePaths';
import { resolveWebURL } from '../resolveWebURL';
import { sanitizeRedirectPath } from '../sanitizeRedirectPath';
import { isNativeOnlyWebviewPath } from './nativeOnlyWebviewPath';

/**
 * 딥링크를 웹 링크로 변환합니다.
 * - WEB_ROUTE_PATHS에 정의된 경로: path 반환 (예: '/goods/123')
 * - 그 외 경로: 접속 환경에 맞는 레거시 웹 풀 URL 반환
 *   (예: 'https://m-stag-4.a-bly.com/today-delivery')
 * - 외부 URL: 풀 URL 그대로 반환
 * - 미지원 딥링크: null 반환
 */
export const convertDeeplinkToWebLink = async (
  deeplink: string,
): Promise<string | null> => {
  const resolved = resolveDeeplinkPath(deeplink);
  if (resolved == null) return null;

  // 레거시 웹 절대 URL은 경로로 환원해 상대 경로 케이스와 동일하게 판정한다.
  // (그대로 두면 prod 호스트가 박힌 config 딥링크가 stage 에서도 prod 웹으로 나간다.)
  const path = toLegacyWebPath(resolved) ?? resolved;
  if (path.startsWith('http')) return path;
  if (isKnownRoute(path.split(/[?#]/)[0])) return path;

  return resolveWebURL(path);
};

/** 레거시 모바일 웹 호스트 (m.a-bly.com, m-stag-4.a-bly.com …) */
const LEGACY_WEB_HOSTNAME_PATTERN = /^m(-[a-z0-9-]+)?\.a-bly\.com$/i;

/**
 * 레거시 모바일 웹 절대 URL이면 경로(+쿼리/해시)로 환원합니다.
 * 외부 URL이거나 이미 경로면 null.
 */
const toLegacyWebPath = (urlOrPath: string): string | null => {
  if (!urlOrPath.startsWith('http')) return null;
  try {
    const { hostname, pathname, search, hash } = new URL(urlOrPath);
    if (!LEGACY_WEB_HOSTNAME_PATTERN.test(hostname)) return null;
    return `${pathname}${search}${hash}`;
  } catch {
    return null;
  }
};

/**
 * 딥링크에서 웹 경로(path) 또는 풀 URL을 추출합니다.
 *
 * 특수 케이스:
 * - 절대 http(s) URL → 우리 레거시 웹 호스트면 경로로 환원, 외부면 그대로
 * - ably://webview → url 파라미터 추출 (네이티브 전용 경로 차단)
 * - ably://home → /
 * - ably://react/App → WEB_ROUTE_PATHS 기반 스크린 매핑
 *
 * 그 외: ably://scheme/path → /scheme/path (자동 매핑)
 */
const resolveDeeplinkPath = (deeplink: string): string | null => {
  // `ably://` 로 감싸지 않은 **순수 절대 URL** 로 들어오는 호출부가 있다(웹뷰 콘텐츠가
  // postMessage 로 보내는 openDeeplink 등). 아래 분기들은 전부 `ably://` 전제라 그대로 두면
  // 맨 끝 fallthrough 가 앞 8글자(`ably://` 길이)를 잘라 `/-stag-4.a-bly.com/...` 같은 쓰레기
  // 경로를 만들고 null 로 떨어진다 → 호출부(`useOpenDeeplink`)가 화면 이동 대신 앱 다운로드
  // 모달을 띄운다. 그래서 여기서 먼저 받는다.
  if (/^https?:\/\//.test(deeplink)) {
    const legacyPath = toLegacyWebPath(deeplink);
    // 외부 URL 은 그대로 넘긴다(`ably://webview?url=<외부>` 와 같은 처리).
    if (legacyPath == null) return deeplink;
    return isNativeOnlyWebviewPath(legacyPath.split(/[?#]/)[0])
      ? null
      : legacyPath;
  }

  if (deeplink.startsWith('ably://webview')) {
    return resolveWebviewPath(deeplink);
  }

  if (deeplink.startsWith('ably://home')) {
    return '/';
  }

  if (deeplink.startsWith('ably://search')) {
    return resolveSearchPath(deeplink);
  }

  if (deeplink.startsWith('ably://main/')) {
    return resolveDeeplinkPath(
      `ably://${deeplink.slice('ably://main/'.length)}`,
    );
  }

  if (deeplink === 'ably://sign/' || deeplink === 'ably://sign') {
    return resolveSignPath('/login');
  }

  if (deeplink === 'ably://sign/in') {
    return resolveSignPath('/login/email');
  }

  if (deeplink.startsWith('ably://react/App')) {
    return getScreenRoutePath(deeplink);
  }

  // 여기부터는 `ably://` 를 잘라내는 전제라 스킴을 먼저 확인한다(아니면 엉뚱한 경로가 된다).
  if (!deeplink.startsWith('ably://')) return null;

  // ably://goods/123?ref=home → /goods/123?ref=home
  const pathAndQuery = deeplink.slice('ably://'.length);
  const path = `/${pathAndQuery}`;
  const pathOnly = path.split('?')[0];
  if (isKnownRoute(pathOnly)) return path;
  return isWebAvailableDeeplinkPath(pathOnly) ? path : null;
};

const resolveWebviewPath = (deeplink: string): string | null => {
  const url = extractWebviewUrl(deeplink);
  if (url == null) return null;

  if (url.startsWith('http')) {
    const pathname = new URL(url).pathname.replace(/\/+/g, '/');
    return isNativeOnlyWebviewPath(pathname) ? null : url;
  }

  const path = `/${url.replace(/^\//, '')}`;
  return isNativeOnlyWebviewPath(path) ? null : path;
};

const getScreenRoutePath = (deeplink: string): string | null => {
  const searchParams = new URLSearchParams(deeplink.split('?')[1]);
  const screen = searchParams.get('screen');
  const extrasRaw = searchParams.get('extras');

  if (!screen) return null;

  const routePattern = WEB_ROUTE_PATHS[screen as keyof typeof WEB_ROUTE_PATHS];
  if (!routePattern) return null;

  if (!extrasRaw) {
    return routePattern.replace(/:[\w]+/g, '').replace(/\/+$/, '') || '/';
  }

  try {
    const extras = JSON.parse(extrasRaw) as Record<string, unknown>;
    const resolved = routePattern.replace(/:(\w+)/g, (_, param) => {
      const value = extras[param] ?? extras['sno'];
      return value != null ? String(value) : '';
    });

    return `${resolved}?extras=${encodeURIComponent(extrasRaw)}`;
  } catch {
    return null;
  }
};

const resolveSearchPath = (deeplink: string): string => {
  const params = new URLSearchParams(deeplink.split('?')[1]);
  const query = params.get('query');

  // query 없음 → 검색 보조(SearchHelper). show_keyboard는 호출자가 명시한 경우에만 보존
  if (query == null) {
    const qs = params.toString();
    return qs
      ? `${WEB_ROUTE_PATHS.SearchHelper}?${qs}`
      : WEB_ROUTE_PATHS.SearchHelper;
  }

  // query 있음 → 검색 결과(SearchResult)로 직행. 키 변환 없이 그대로 전달
  return `${WEB_ROUTE_PATHS.SearchResult}?${params.toString()}`;
};

/**
 * 로그인 경로에 복귀 경로(?redirect=)를 싣습니다.
 * - 절대 URL(href)이 아니라 상대 경로: 소비측이 내부 경로만 허용합니다.
 * - location 이 없으면(네이티브) 복귀 경로 없이 로그인 경로만 반환합니다.
 */
const resolveSignPath = (loginPath: string): string => {
  const location = typeof window === 'undefined' ? undefined : window.location;
  if (location == null) return loginPath;

  const redirect = sanitizeRedirectPath(
    `${location.pathname}${location.search}`,
  );
  return `${loginPath}?redirect=${encodeURIComponent(redirect)}`;
};

/** 웹에서 지원하는 딥링크 경로 (ably://path → /path 자동 매핑 허용) */
const WEB_AVAILABLE_DEEPLINK_PATHS = [
  '/mypage',
  '/fun',
  '/events',
  '/cart',
  '/notifications',
  '/setting',
  '/style',
  '/overview',
];

const isWebAvailableDeeplinkPath = (path: string): boolean =>
  WEB_AVAILABLE_DEEPLINK_PATHS.some(prefix => path.startsWith(prefix));

const extractWebviewUrl = (deeplink: string): string | null => {
  const queryPart = deeplink.split('?').slice(1).join('?');
  const params = new URLSearchParams(queryPart);
  return params.get('url');
};
